import {inject, INJECTOR} from "@angular/core";
import {MarkupManagerService} from "@wms/core";
import { WmsCanvasDomComponent } from "@wms/design-container";
import { get } from 'lodash-es';
import { PropertiesPanelComponent } from "@wms/properties-panel";
import { PropertyController } from "@wms/app/properties-panel/propertyController";
import { DbCanvasDomComponent } from "./workspace/database-workspace/components/db-canvas-dom/db-canvas-dom.component";

interface stackObj {
  controller: any;
  name: string;
  preState: any;
  postState: any;
}

export class WmCommandProvider {
  private redoStack: Array<stackObj> = [];
  private undoStack: Array<stackObj> = [];

  constructor() {
  }

  isDirty() {
    return this.undoStack.length !== 0;
  }

  // responsible for capturing states(preState, postState) and calling the given function
  execute(ref: any, comp: WmsCanvasDomComponent | PropertiesPanelComponent | DbCanvasDomComponent, fnName: string, data: any) {
    let preState,
    postState,
    obj;

    preState = ref.savePreState(fnName, data, comp as any);
    get(ref, fnName).call(this, preState, comp);
    postState = ref.savePostState(fnName, preState);
    obj = {
      "controller": ref,
      "name": fnName,
      "preState": preState,
      "postState": postState
    };
    this.undoStack.push(obj);
    this.redoStack = [];
  }

  // undo the last task
  undo(propertiesPanelcomp?: PropertiesPanelComponent, canvasDomComp?: WmsCanvasDomComponent) {
    if (this.isUndoPossible()) {
      // @ts-ignore
      let undoObj: stackObj = this.undoStack.pop(),
      controller = undoObj.controller, comp;
      if (controller instanceof PropertyController) {
        comp = propertiesPanelcomp;
      } else {
        comp = canvasDomComp;
      }
      controller.undo.call(this, undoObj.name, undoObj.postState, comp);
      if (!this.isRedoPossible()) {
      this.redoStack.pop();
    }
    this.redoStack.push(undoObj);
    if (controller.continue && controller.continue(undoObj.postState)) {
      this.undo();
    }
    }
  }

  redo(propertiesPanelcomp?: PropertiesPanelComponent, canvasDomComp?: WmsCanvasDomComponent) {
    if (this.isRedoPossible()) {
      // @ts-ignore
      let redoObj: stackObj = this.redoStack.pop(),
      controller = redoObj.controller,
      nextRedoObj, comp;
      if (controller instanceof PropertyController) {
        comp = propertiesPanelcomp;
      } else {
        comp = canvasDomComp;
      }
      controller.redo.call(this, redoObj.name, redoObj.preState, comp);
      this.undoStack.push(redoObj);
      if (this.isRedoPossible()) {
        nextRedoObj = this.redoStack[this.redoStack.length - 1];
        if (nextRedoObj.controller.continue
      && nextRedoObj.controller.continue(nextRedoObj.preState)) {
          this.redo();
        }
      }
    }
  }

  reset() {
    this.redoStack = [];
    this.undoStack = [];
  }

  isUndoPossible() {
    let isUndoPossible = this.undoStack.length > 0;
    return isUndoPossible;
  }

getUndoStackLength(){
    return this.undoStack.length
}
  isRedoPossible() {
    return this.redoStack.length > 0;
  }

  getLastUndo() {
    if (this.isUndoPossible()) {
      let last = (this.undoStack.length - 1);
      return this.undoStack[last].name;
    }
    return;
  }

  getLastRedo() {
    if (this.isRedoPossible()) {
      let last = (this.redoStack.length - 1);
      return this.redoStack[last].name;
    }
    return;
  }
}

let self: CommandController;

export class CommandController {
  private preStateMarkup: any;
  private preStateClass: any;

  constructor(private markupManager?: MarkupManagerService) {
    self = this;
  }

  // preState data is generated depending on the actionName param
  generatePreStateData(name: string, objData: any) {
    if (name === 'switchTemplate') {
      self.preStateMarkup = self.markupManager?.getActiveMarkupEl();
    }

    if (name === 'switchLayout') {
      self.preStateClass = self.markupManager?.getActiveMarkupEl()?.attr('class');
    }
    return objData;
  }

  // postState data is generated depending on the actionName param
  generatePostStateData(name: string, data: any) {
    if (name === 'switchTemplate') {
      return {
        markup: self.preStateMarkup,
        pageName: data.pageName
      };
    }

    if (name === 'switchLayout') {
      return {
        class: self.preStateClass,
        pageName: data.pageName
      };
    }

    return data;
  }

  swapProperties(data: {[key: string]: string}, property1: string, property2: string) {
    let temp = data[property1];
    data[property1] = data[property2];
    data[property2] = temp;
  }

  // For move widget, we swap the values of the referenceId, referencePos and parent Id. This is needed for undo/redo
  processMoveForUndoRedo(data: any) {
    self.swapProperties(data, 'referenceId', 'prevReferenceId');
    self.swapProperties(data, 'referencePos', 'prevReferencePos');
    self.swapProperties(data, 'parentId', 'prevParentId');
  }

  // undo tasks are delegated based on the actionName
  undoFunc(actionName: string, data: any, ref: WmsCanvasDomComponent) {
    switch (actionName) {
      case "addWidget":
        self.deleteWidget(ref.getWidgetDetails(data), ref);
        break;
      case "deleteWidget":
        self.addWidget(data, ref);
        break;
      case "moveWidget":
        self.processMoveForUndoRedo(data);
        self.moveWidget(data, ref);
        break;
      case "switchTemplate":
        self.switchTemplate(data, ref);
        break;
      case "switchLayout":
        self.switchLayout(data, ref);
        break;
    }
  }

  // undo tasks are delegated based on the actionName
  redoFunc(actionName: string, data: any, ref: WmsCanvasDomComponent) {
    switch (actionName) {
      case "addWidget":
        self.addWidget(data, ref);
        break;
      case "deleteWidget":
        self.deleteWidget(ref.getWidgetDetails(data), ref);
        break;
      case "moveWidget":
        self.processMoveForUndoRedo(data);
        self.moveWidget(data, ref);
        break;
      case "switchTemplate":
        self.switchTemplate(data, ref);
        break;
      case "switchLayout":
        self.switchLayout(data, ref);
        break;
    }
  }

  addWidget(data: any, ref: WmsCanvasDomComponent) {
    ref.addWidget(data, true, data.preventSelection);
  }
  deleteWidget (data: any, ref: WmsCanvasDomComponent) {
    ref.deleteWidget(data, true);
  }
  moveWidget(data: any, ref: WmsCanvasDomComponent) {
    ref.moveWidget(data, true);
  }
  savePreState(name: string, data: any, comp: WmsCanvasDomComponent) {
    return self.generatePreStateData(name, data);
  }
  savePostState(name: string, data: any) {
    return self.generatePostStateData(name, data);
  }
  switchTemplate(data: any, ref: WmsCanvasDomComponent) {
    ref.switchTemplate(data);
  }
  switchLayout(data: any, ref: WmsCanvasDomComponent) {
    ref.switchLayout(data);
  }
  continue(data: any) {
    return data.isAtomicAction === false;
  }
  undo(name: string, data: any, ref: WmsCanvasDomComponent) {
    self.undoFunc(name, data, ref);
  }
  redo(name: string, data: any, ref: WmsCanvasDomComponent) {
    self.redoFunc(name, data, ref);
  }
}

